import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  inject,
  input,
} from '@angular/core';

import { GradeLevelRef, SchoolLevelPoint } from '../report.model';
import { ChartTheme, paletteFor } from './chart-palette';
import {
  AxisTick,
  ChartPoint,
  GridLine,
  PlotMargin,
  bandCentre,
  clamp,
  displayCode,
  monotonePath,
  plotBox,
  round,
  yTickX,
} from './chart-geometry';
import {
  ChartTooltipService,
  chartWidthSignal,
  compactViewportSignal,
} from './chart-tooltip.service';

const MARGIN: PlotMargin = { top: 16, right: 8, bottom: 4, left: 8 };
/**
 * Assez large pour le plus long libellé du référentiel — « 4ème année
 * secondaire (Bac) » / « السنة الرابعة ثانوي (بكالوريا) » à 12 px.
 */
const AXIS_WIDTH = 182;
const X_AXIS_HEIGHT = 30;
/**
 * Écart minimal entre deux libellés d'ordonnée. À 12 px de fonte, en deçà les
 * hampes se touchent et deux niveaux voisins se lisent comme un seul mot.
 */
const LABEL_MIN_GAP = 18;
/** Largeur de repli tant que le conteneur n'est pas mesuré. */
const FALLBACK_WIDTH = 920;

interface SchoolLevelDotView {
  code: string;
  label: string;
  levelCode: string;
  levelLabel: string;
  cx: number;
  cy: number;
  r: number;
  hitR: number;
  fill: string;
}

/**
 * Niveau scolaire de chaque compétence, en courbe.
 *
 * L'axe des ordonnées est **ordinal** : le rang d'un niveau dans le référentiel
 * renvoyé par l'API, plus aucune liste de dix niveaux codée en dur. Déduire
 * l'ordre du libellé traduit ne marcherait dans aucune des trois langues, et le
 * référentiel est paramétrable en base.
 *
 * Ce graphique dit d'où vient une lacune, pas comment l'élève s'en est sorti :
 * ni note, ni verdict d'acquisition nulle part, y compris dans l'infobulle.
 */
@Component({
  selector: 's2e-school-level-chart',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[style.display]': "'block'" },
  template: `
    <svg
      class="chart-svg school-level__svg"
      [attr.width]="width()"
      [attr.height]="height()"
      [attr.viewBox]="viewBox()"
      role="img"
      [attr.aria-label]="ariaLabel() || null"
      focusable="false"
      (mouseleave)="tooltip.hide()"
    >
      <g class="chart-grid">
        @for (line of gridLines(); track line.key) {
          <line
            [attr.x1]="line.x1"
            [attr.y1]="line.y1"
            [attr.x2]="line.x2"
            [attr.y2]="line.y2"
            [attr.stroke]="palette().grid"
            stroke-width="1"
          ></line>
        }
      </g>
      <line
        [attr.x1]="baseline().x1"
        [attr.y1]="baseline().y1"
        [attr.x2]="baseline().x2"
        [attr.y2]="baseline().y2"
        [attr.stroke]="palette().axis"
        stroke-width="1"
      ></line>
      <g class="chart-axis chart-axis--y">
        @for (tick of yTicks(); track tick.key) {
          <text
            [attr.x]="tick.x"
            [attr.y]="tick.y"
            text-anchor="end"
            dominant-baseline="central"
            [attr.fill]="palette().textMuted"
            font-size="12"
          >{{ tick.label }}</text>
        }
      </g>
      <g class="chart-axis chart-axis--x">
        @for (tick of xTicks(); track tick.key) {
          <text
            [attr.x]="tick.x"
            [attr.y]="tick.y"
            text-anchor="middle"
            dominant-baseline="central"
            [attr.fill]="palette().textSecondary"
            [attr.font-size]="codeFont()"
            [attr.direction]="codeDir()"
          >{{ tick.label }}</text>
        }
      </g>
      <path
        class="school-level__line"
        [attr.d]="curve()"
        fill="none"
        [attr.stroke]="palette().categorical[0]"
        stroke-width="2.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      ></path>
      <g class="school-level__dots">
        @for (dot of dots(); track dot.code) {
          <circle
            class="school-level__dot school-level__point"
            [attr.data-code]="dot.code"
            [attr.data-level]="dot.levelCode"
            [attr.cx]="dot.cx"
            [attr.cy]="dot.cy"
            [attr.r]="dot.r"
            [attr.fill]="dot.fill"
            [attr.stroke]="palette().surface"
            stroke-width="1.5"
          ></circle>
        }
      </g>
      <g class="school-level__points">
        @for (dot of dots(); track dot.code) {
          <circle
            class="school-level__point"
            [attr.data-code]="dot.code"
            [attr.data-level]="dot.levelCode"
            [attr.cx]="dot.cx"
            [attr.cy]="dot.cy"
            [attr.r]="dot.hitR"
            fill="transparent"
            (mouseenter)="enter(dot, $event)"
            (mousemove)="tooltip.move($event)"
          ></circle>
        }
      </g>
    </svg>
  `,
})
export class SchoolLevelChartComponent {
  readonly tooltip = inject(ChartTooltipService);

  readonly points = input.required<readonly SchoolLevelPoint[]>();
  /** Référentiel ordonné des niveaux ; il définit l'échelle à lui seul. */
  readonly scale = input.required<readonly GradeLevelRef[]>();
  readonly theme = input.required<ChartTheme>();
  readonly rtl = input.required<boolean>();
  readonly codeDir = input.required<'rtl' | 'ltr'>();
  /** « Niveau scolaire » — libellé de la ligne d'infobulle, déjà traduit. */
  readonly levelLabelHeading = input.required<string>();

  /** Libellé accessible complet, déjà traduit par la section. */
  readonly ariaLabel = input('');
  /** Préfixe de code affiché. `data-code` garde le code brut. */
  readonly codePrefix = input('C');

  private readonly compact = compactViewportSignal();
  private readonly measured = chartWidthSignal(FALLBACK_WIDTH);

  readonly width = computed(() => this.measured());
  readonly height = computed(() => (this.compact() ? 340 : 420));
  readonly viewBox = computed(() => `0 0 ${this.width()} ${this.height()}`);
  readonly palette = computed(() => paletteFor(this.theme()));

  private readonly box = computed(() =>
    plotBox(this.width(), this.height(), MARGIN, AXIS_WIDTH, X_AXIS_HEIGHT, this.rtl()),
  );

  /** Ordonnée d'un rang, compté à partir de 1 au bas de l'échelle. */
  private yFor(rank: number): number {
    const box = this.box();
    const levels = Math.max(1, this.scale().length);
    return box.bottom - (rank / levels) * box.height;
  }

  /**
   * Ordre du cursus, miroité en RTL comme les autres graphiques cartésiens :
   * la première compétence part de la droite en arabe, de la gauche ailleurs,
   * pour que la courbe file toujours dans le sens de lecture.
   */
  private readonly ordered = computed(() => {
    const list = this.points().slice();
    return this.rtl() ? list.reverse() : list;
  });

  private readonly band = computed(() => {
    const count = this.ordered().length;
    return count === 0 ? 0 : this.box().width / count;
  });

  /**
   * Tous les codes restent sur l'axe — un par marqueur — et c'est l'étiquette
   * qui rétrécit avec la bande, plutôt que d'en supprimer une sur deux. Cela se
   * joue à l'impression : la colonne papier fait 616 px contre 900 à l'écran, ce
   * qui ramène la bande à ~17 px, où « C26 » à la taille écran chevaucherait
   * ses voisines.
   */
  readonly codeFont = computed(() => round(clamp((this.band() - 2) / 1.9, 7.5, 11)));

  /** Les marqueurs rétrécissent aussi, sinon 26 points forment une corde. */
  private readonly dotRadius = computed(() => clamp(this.band() / 7, 2.5, 4.5));

  /**
   * Rangs annotés sur l'axe des ordonnées.
   *
   * <p>Le référentiel compte plusieurs dizaines de niveaux — quarante-deux sur
   * le parcours mathématiques — pour moins de quatre cents pixels de hauteur
   * utile. Tout étiqueter empile les libellés jusqu'à l'illisible, et la grille
   * qui les accompagne vire à l'aplat.</p>
   *
   * <p>On garde donc les deux extrémités de l'échelle, qui en disent l'étendue,
   * puis les niveaux où une compétence est effectivement rattachée — les seuls
   * que le lecteur ait une raison de chercher. Un rang n'est retenu que s'il
   * laisse aux libellés déjà placés la place de s'écrire.</p>
   *
   * <p>Les points ne bougent pas : c'est l'annotation qui s'éclaircit, jamais
   * l'échelle. Une compétence reste à la hauteur de son niveau, étiqueté ou
   * non, et l'infobulle le nomme au survol.</p>
   */
  /**
   * Les niveaux annotés : **ceux que porte une compétence, et eux seuls**.
   *
   * <p>Les deux bornes du référentiel — rangs 1 et N — étaient annotées
   * d'office. Un bilan dont les neuf compétences tiennent toutes autour de
   * « grades 6/7 » affichait donc « 3ème année » et « 3ème année Économie et
   * Gestion » : deux extrémités de cursus qui ne disent rien de l'élève, et
   * que rien ne distinguait des niveaux réellement mesurés. Une graduation
   * d'échelle se lisait comme une donnée.</p>
   *
   * <p>Elles faisaient pire que s'afficher : en occupant la place les
   * premières, elles <b>évinçaient</b> un niveau réel tombant à moins de
   * {@link LABEL_MIN_GAP} d'une borne. Le seul niveau que le lecteur cherchait
   * pouvait ainsi disparaître au profit d'un niveau où rien n'a été évalué.</p>
   *
   * <p>L'échelle, elle, ne change pas : la hauteur d'un point reste son rang
   * dans le référentiel complet, donc la distance entre deux niveaux garde son
   * sens. Seules les étiquettes se limitent à ce qui est mesuré.</p>
   */
  private readonly annotatedRanks = computed<ReadonlySet<number>>(() => {
    const total = this.scale().length;
    if (total === 0) return new Set<number>();

    const utilises = [...new Set(this.points().map((p) => p.rank))]
      .filter((rank) => rank >= 1 && rank <= total)
      .sort((a, b) => a - b);

    const retenus = new Set<number>();
    const occupees: number[] = [];

    for (const rang of utilises) {
      const y = this.yFor(rang);
      if (occupees.every((prise) => Math.abs(prise - y) >= LABEL_MIN_GAP)) {
        retenus.add(rang);
        occupees.push(y);
      }
    }

    return retenus;
  });

  readonly gridLines = computed<GridLine[]>(() => {
    const box = this.box();
    const annotes = this.annotatedRanks();
    // La grille suit les graduations : une ligne sans libellé ne repère rien,
    // et quarante d'entre elles effacent la courbe qu'elles devaient servir.
    return this.scale()
      .map((level, index) => ({ level, rank: index + 1 }))
      .filter(({ rank }) => annotes.has(rank))
      .map(({ level, rank }) => {
        const y = round(this.yFor(rank));
        return { key: level.code, x1: round(box.left), y1: y, x2: round(box.right), y2: y };
      });
  });

  readonly baseline = computed(() => {
    const box = this.box();
    const y = round(box.bottom);
    return { x1: round(box.left), y1: y, x2: round(box.right), y2: y };
  });

  /**
   * Graduations d'ordonnée, ancrées `end` **dans les deux directions**, et ce
   * n'est pas une coquille : SVG résout `text-anchor` contre la direction de
   * base du texte. `end` désigne donc le bord droit en LTR (l'étiquette part
   * vers la gauche, du côté où l'axe se trouve) et le bord gauche en RTL.
   * `start` pousserait ces libellés très longs en travers des colonnes.
   */
  readonly yTicks = computed<AxisTick[]>(() => {
    const box = this.box();
    const rtl = this.rtl();
    const annotes = this.annotatedRanks();
    return this.scale()
      .map((level, index) => ({ level, rank: index + 1 }))
      .filter(({ rank }) => annotes.has(rank))
      .map(({ level, rank }) => ({
        key: level.code,
        x: yTickX(box, rtl),
        y: round(this.yFor(rank)),
        label: level.label,
        anchor: 'end' as const,
      }));
  });

  readonly xTicks = computed<AxisTick[]>(() => {
    const box = this.box();
    const band = this.band();
    const prefix = this.codePrefix();
    return this.ordered().map((point, index) => ({
      key: point.code,
      x: round(bandCentre(box, band, index)),
      y: round(box.bottom + 16),
      label: displayCode(point.code, prefix),
      anchor: 'middle' as const,
    }));
  });

  /**
   * Marqueurs, avec leur disque de survol.
   *
   * Une compétence de rang 0 ne porte aucun niveau du référentiel : elle garde
   * sa place sur l'axe des abscisses (les codes restent tous alignés) mais ne
   * reçoit ni marqueur ni sommet de courbe. La poser à l'ordonnée 0 la
   * afficherait sur la ligne de base, ce qui se lirait comme « niveau zéro »
   * alors que l'information est simplement absente.
   *
   * Le disque de survol est en `fill="transparent"` et non `fill="none"` : un
   * remplissage `none` n'est pas peint et ne reçoit aucun événement de
   * pointeur, ce qui est précisément son unique raison d'être.
   */
  readonly dots = computed<SchoolLevelDotView[]>(() => {
    const box = this.box();
    const band = this.band();
    if (band === 0) return [];

    const scale = this.scale();
    const r = round(this.dotRadius());
    const hitR = round(Math.max(9, band / 2));

    return this.ordered().flatMap((point, index) => {
      if (point.rank <= 0) return [];
      const level = scale[point.rank - 1];
      return [
        {
          code: point.code,
          label: point.label,
          levelCode: level ? level.code : '',
          levelLabel: point.levelLabel,
          cx: round(bandCentre(box, band, index)),
          cy: round(this.yFor(point.rank)),
          r,
          hitR,
          fill: point.color,
        },
      ];
    });
  });

  readonly curve = computed(() => {
    const vertices: ChartPoint[] = this.dots().map((dot) => ({ x: dot.cx, y: dot.cy }));
    return monotonePath(vertices);
  });

  enter(dot: SchoolLevelDotView, event: MouseEvent): void {
    /* Code, intitulé, niveau. Toujours ni note ni verdict : ce graphique situe
       la compétence dans le cursus, il ne dit rien de la performance. */
    this.tooltip.show(
      `${displayCode(dot.code, this.codePrefix())} · ${dot.label}`,
      [{ label: this.levelLabelHeading(), value: dot.levelLabel, color: dot.fill }],
      event,
    );
  }
}
